// ChatPill — the floating "Ask pm-tools" pill (UI_SPEC §1.18.a) + the
// 480 px slide-over panel (§1.18.b).
//
// Pill: bottom-right, z-40, slate-900 background, white text, hover →
//       slate-700. Click toggles the slide-over open.
// Slide-over: right-aligned, full-height, 480 px wide, white surface
//       with a slate-200 border on the left. Backdrop dim at 30 %.
//       ESC and backdrop click close it.

import { useEffect, useState } from "react";
import { ChatPane } from "./ChatPane.js";
import { useShell } from "./ShellContext.js";

export function ChatPill() {
  const { chatOpen, openChat, closeChat, chat, prefill, setPrefill } = useShell();
  const [input, setInput] = useState("");

  // When the parent sets a prefill, push it into the composer's input.
  useEffect(() => {
    if (prefill) {
      setInput(prefill);
      setPrefill("");
    }
  }, [prefill, setPrefill]);

  // ESC closes the slide-over; Cmd/Ctrl-K toggles it open. The accelerator
  // matches the kbd hint on the floating pill.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (chatOpen && e.key === "Escape") {
        closeChat();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (chatOpen) closeChat();
        else openChat();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chatOpen, closeChat, openChat]);

  return (
    <>
      {/* Pill (always present; hidden behind backdrop when open). */}
      <button
        type="button"
        onClick={() => openChat()}
        className="fixed bottom-4 right-4 z-30 flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-medium text-white shadow-lg ring-1 ring-slate-700/50 transition-colors hover:bg-slate-700"
        title="Ask pm-tools (or Cmd-K)"
      >
        <span aria-hidden>✦</span>
        <span>Ask pm-tools</span>
        <kbd className="rounded bg-white/15 px-1.5 py-0.5 font-mono text-[10px] tracking-wider">⌘K</kbd>
      </button>

      {/* Backdrop + slide-over. */}
      {chatOpen && (
        <>
          <button
            type="button"
            aria-label="Close chat"
            className="fixed inset-0 z-40 bg-slate-950/50 transition-opacity"
            onClick={closeChat}
          />
          <div className="fixed inset-y-0 right-0 z-50 flex w-[480px] max-w-full flex-col border-l border-slate-200 bg-white shadow-2xl">
            <header className="flex h-12 flex-shrink-0 items-center justify-between border-b border-slate-200 bg-slate-50 px-4">
              <div className="flex items-center gap-2">
                <span aria-hidden className="text-slate-500">✦</span>
                <span className="text-sm font-semibold text-slate-800">Ask pm-tools</span>
                {chat.sending && (
                  <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                    streaming
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={closeChat}
                className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                aria-label="Close"
              >✕</button>
            </header>
            <div className="flex-1 overflow-hidden bg-white">
              <ChatPane
                messages={chat.messages}
                sending={chat.sending}
                onSend={chat.send}
                inputValue={input}
                onInputChange={setInput}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}
